/**
 * Typed-row primitives + helpers for editing a single IDynamicLottieChange.
 *
 * Pure TS (no Angular), so any host UI can reuse them: the editor dialog renders
 * a long list of these rows, and the project-template form needs a single
 * inline row per slot. Each row class owns:
 *   - the user-facing display state (a chip value or a slider/hex/toggle state),
 *   - the conversion between that display state and the underlying
 *     IDynamicLottieChange.value (always written via the row's on…() methods),
 *   - a discriminating `kind` literal so a template can pick the right control
 *     with `*ngIf="row.kind === 'x'"` and have Angular's strict type narrowing
 *     recognise the kind-specific properties.
 *
 * Row construction goes through buildEditorRows() at module scope — it walks
 * the changes once, collapses R/G/B triplets into a single RgbRow, and picks
 * the right row class per change based on classifyInputKind() (color/number/
 * toggle/text). Callers that need a single row for a single change should use
 * buildSingleRow() instead.
 */

import { DynamicItemType, IDynamicLottieChange, TEXT_DYNAMIC_ITEM_TYPES } from 'lottie-json-helper/lib/types';

// ────────────────────────────────────────────────────────────────────────────
// Row classes
// ────────────────────────────────────────────────────────────────────────────

export type EditorRow = TextRow | NumberRow | ColorRow | ToggleRow | RgbRow;

export interface NumberRange {
    min: number;
    max: number;
    step: number;
}

export abstract class BaseRow {
    abstract readonly kind: 'text' | 'number' | 'color' | 'toggle' | 'rgb';
    constructor(
        public readonly label: string,
        public readonly tooltip: string
    ) {}
}

export class TextRow extends BaseRow {
    readonly kind = 'text' as const;
    value: string;
    /** Truncation-friendly display for the resting chip (also doubles as tooltip). */
    displayValue: string;
    /** Resting (false) vs. expanded input (true). Click reveals the input. */
    editing = false;

    constructor(
        label: string,
        tooltip: string,
        private readonly change: IDynamicLottieChange
    ) {
        super(label, tooltip);
        this.value = String(change.value ?? '');
        this.displayValue = formatTextForDisplay(this.value);
    }

    onValueChange(v: string): void {
        this.value = v;
        this.displayValue = formatTextForDisplay(v);
        this.change.value = v;
    }

    startEdit(): void {
        this.editing = true;
    }

    onInputBlur(): void {
        this.editing = false;
    }

    onEditKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape' || event.key === 'Enter') {
            this.editing = false;
            (event.target as HTMLElement | null)?.blur();
        }
    }
}

export class NumberRow extends BaseRow {
    readonly kind = 'number' as const;
    value: number;
    displayValue: string;
    editing = false;

    constructor(
        label: string,
        tooltip: string,
        public readonly range: NumberRange,
        private readonly change: IDynamicLottieChange
    ) {
        super(label, tooltip);
        this.value = toNumber(change.value);
        this.displayValue = formatNumberForDisplay(this.value);
    }

    onValueChange(v: number | string): void {
        const n = toNumber(v);
        this.value = n;
        this.displayValue = formatNumberForDisplay(n);
        this.change.value = n;
    }

    startEdit(): void {
        this.editing = true;
    }

    /**
     * Exit edit mode when focus moves OUTSIDE the slider wrapper. Tabbing
     * between the range slider and the numeric readout keeps us in edit mode
     * because `relatedTarget` still lives inside the wrapper.
     */
    endEditIfFocusLeft(event: FocusEvent): void {
        const wrapper = event.currentTarget as HTMLElement | null;
        const next = event.relatedTarget as Node | null;
        if (!wrapper || !next || !wrapper.contains(next)) {
            this.editing = false;
        }
    }

    onEditKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape' || event.key === 'Enter') {
            this.editing = false;
            (event.target as HTMLElement | null)?.blur();
        }
    }
}

export class ColorRow extends BaseRow {
    readonly kind = 'color' as const;
    hex: string;
    constructor(
        label: string,
        tooltip: string,
        private readonly change: IDynamicLottieChange
    ) {
        super(label, tooltip);
        this.hex = rgba01ArrayToHex(change.value);
    }
    onHexChange(hex: string): void {
        this.hex = hex;
        const prev = Array.isArray(this.change.value) ? this.change.value : [0, 0, 0, 1];
        const a = Number(prev[3] ?? 1);
        this.change.value = hexToRgba01(hex, Number.isFinite(a) ? a : 1);
    }
}

export class ToggleRow extends BaseRow {
    readonly kind = 'toggle' as const;
    on: boolean;
    constructor(
        label: string,
        tooltip: string,
        private readonly change: IDynamicLottieChange
    ) {
        super(label, tooltip);
        this.on = toNumber(change.value) >= 0.5;
    }
    onToggle(on: boolean): void {
        this.on = on;
        this.change.value = on ? 1 : 0;
    }
}

/**
 * Collapsed R/G/B slider triplet (e.g. TextR0/TextG0/TextB0) rendered as a
 * single color row. Mutates the three underlying changes in lock-step.
 */
export class RgbRow extends BaseRow {
    readonly kind = 'rgb' as const;
    hex: string;
    constructor(
        label: string,
        tooltip: string,
        private readonly r: IDynamicLottieChange,
        private readonly g: IDynamicLottieChange,
        private readonly b: IDynamicLottieChange
    ) {
        super(label, tooltip);
        this.hex = rgbTripletToHex(r.value, g.value, b.value);
    }
    onHexChange(hex: string): void {
        this.hex = hex;
        const h = normalizeHex6(hex);
        this.r.value = parseInt(h.slice(0, 2), 16);
        this.g.value = parseInt(h.slice(2, 4), 16);
        this.b.value = parseInt(h.slice(4, 6), 16);
    }
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers (module scope; no class dependencies)
// ────────────────────────────────────────────────────────────────────────────

const COLOR_TYPES: ReadonlySet<string> = new Set([
    DynamicItemType.FILL,
    DynamicItemType.STROKE,
    DynamicItemType.GRADIENT,
    DynamicItemType.COLOR_RGB,
]);

const KNOWN_NUMBER_TYPES: ReadonlySet<string> = new Set([
    DynamicItemType.FONT_SIZE,
    DynamicItemType.FRAME,
    DynamicItemType.IMG_DARKEN,
    DynamicItemType.IMG_COLOR_OVERLAY,
    DynamicItemType.MAX_CHR,
    'slider',
    'number',
]);

export function toNumber(v: unknown): number {
    const n = typeof v === 'number' ? v : parseFloat(v as any);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Slick numeric display: drops trailing zeros, snaps to integer when the value
 * is within ½ a hundredth, otherwise up to 2 decimals.
 *   0       -> "0"
 *   50      -> "50"
 *   1.5     -> "1.5"
 *   25.001  -> "25"
 *   4.236   -> "4.24"
 */
export function formatNumberForDisplay(n: number): string {
    if (!Number.isFinite(n)) return '—';
    const rounded = Math.round(n);
    if (Math.abs(n - rounded) < 0.005) return String(rounded);
    return n.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Text readout: keeps the value verbatim (CSS truncates with ellipsis), but
 * normalises empty/whitespace to an em-dash placeholder so the chip never
 * collapses to a clickable zero-pixel target.
 */
export function formatTextForDisplay(s: string): string {
    return s && s.trim() !== '' ? s : '—';
}

function hex2(n: number, max = 255): string {
    return Math.round(Math.max(0, Math.min(max, n)))
        .toString(16)
        .padStart(2, '0');
}

export function normalizeHex6(hex: string): string {
    return (hex || '').replace('#', '').padEnd(6, '0').slice(0, 6);
}

export function rgba01ArrayToHex(value: unknown): string {
    if (!Array.isArray(value)) return '#000000';
    return '#' + hex2(toNumber(value[0]) * 255) + hex2(toNumber(value[1]) * 255) + hex2(toNumber(value[2]) * 255);
}

export function hexToRgba01(hex: string, alpha = 1): [number, number, number, number] {
    const h = normalizeHex6(hex);
    return [
        parseInt(h.slice(0, 2), 16) / 255,
        parseInt(h.slice(2, 4), 16) / 255,
        parseInt(h.slice(4, 6), 16) / 255,
        alpha,
    ];
}

export function rgbTripletToHex(r: unknown, g: unknown, b: unknown): string {
    return '#' + hex2(toNumber(r)) + hex2(toNumber(g)) + hex2(toNumber(b));
}

/** What input control fits a change. Used at row-build time. */
export type InputKind = 'text' | 'number' | 'color' | 'toggle';

/**
 * Permissive "does this value read as a number" test — INCLUDES sniffing a
 * numeric string ("255" -> true). Used ONLY for RGB-triplet channel grouping
 * (tryFindRgbTriplet), where we deliberately want to collapse numeric-string
 * R/G/B channels into one color picker.
 *
 * This is NOT the rule for the user-facing control: classifyInputKind() never
 * promotes a bare numeric string to a number, so a text field (e.g. an Address
 * holding "123") can't accidentally render as a slider.
 */
function valueLooksNumeric(c: IDynamicLottieChange): boolean {
    const valueType = (c as any).valueType as string | undefined;
    const hierarchy = (c as any).hierarchy as string | undefined;
    return (
        valueType === 'number' ||
        hierarchy === 'slider' ||
        KNOWN_NUMBER_TYPES.has(c.type) ||
        typeof c.value === 'number' ||
        (typeof c.value === 'string' && c.value !== '' && Number.isFinite(parseFloat(c.value as any)))
    );
}

/**
 * Pick the control kind for a change. Decision order matters: a field becomes a
 * slider/number ONLY when something positively declares it numeric — a token
 * `valueType`/`hierarchy`, a number-by-nature `type`, or an actual number value.
 * Everything else — including a numeric-looking *string* with no declared type —
 * falls through to the text-box default. A text box can safely hold any value;
 * a slider cannot hold text, so text is the correct, lossless fallback.
 */
export function classifyInputKind(c: IDynamicLottieChange): InputKind {
    const valueType = (c as any).valueType as string | undefined;
    const hierarchy = (c as any).hierarchy as string | undefined;

    // Color: declared color type, hex valueType, fill/stroke/gradient hierarchy,
    // or an rgb(a) array value.
    if (COLOR_TYPES.has(c.type)) return 'color';
    if (valueType === 'hex' || hierarchy === 'fill' || hierarchy === 'stroke' || hierarchy === 'gradient') {
        return 'color';
    }
    if (Array.isArray(c.value) && (c.value.length === 3 || c.value.length === 4)) return 'color';

    // Declared text wins over any numeric-looking value: a string/text field
    // must never render as a slider just because its value parses as a number.
    if (valueType === 'string' || hierarchy === 'text' || TEXT_DYNAMIC_ITEM_TYPES.includes(c.type)) {
        return 'text';
    }

    // Number / toggle: only on a positive numeric signal — never sniff a string.
    const isNumeric =
        valueType === 'number' ||
        hierarchy === 'slider' ||
        KNOWN_NUMBER_TYPES.has(c.type) ||
        typeof c.value === 'number';
    if (isNumeric) {
        const range = (c as any).range as [number, number] | null | undefined;
        if (range && range[0] === 0 && range[1] === 1) return 'toggle';
        return 'number';
    }

    // Fallback: text box — the safe default that holds numbers and text alike.
    return 'text';
}

export function deriveRange(c: IDynamicLottieChange): NumberRange {
    const r = (c as any).range as [number, number] | null | undefined;
    if (r && Number.isFinite(r[0]) && Number.isFinite(r[1])) {
        const [min, max] = r;
        const span = Math.abs(max - min);
        const step = span >= 100 ? 1 : span >= 10 ? 0.5 : span >= 1 ? 0.01 : 0.001;
        return { min, max, step };
    }
    switch (c.type) {
        case DynamicItemType.FONT_SIZE:
            return { min: 0, max: 20, step: 0.1 };
        case DynamicItemType.MAX_CHR:
            return { min: 0, max: 200, step: 1 };
        case DynamicItemType.IMG_DARKEN:
        case DynamicItemType.IMG_COLOR_OVERLAY:
        case DynamicItemType.FRAME:
            return { min: 0, max: 100, step: 1 };
        default:
            return { min: 0, max: 100, step: 0.5 };
    }
}

/**
 * "sfl_imgOpacity0" -> "Img Opacity"; "BoxX0" -> "Box X". Strips trailing
 * digits, splits camel/snake case, title-cases.
 */
export function humanize(id: string | undefined | null): string {
    if (!id) return '';
    let s = id;
    if (s.startsWith('sfl_')) s = s.slice(4);
    s = s
        .replace(/\d+$/, '')
        .replace(/_+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    if (!s) return '';
    return s
        .split(' ')
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');
}

export function displayLabel(c: IDynamicLottieChange): string {
    return (c.placeholder && c.placeholder.trim()) || humanize(c.id) || c.id || '';
}

/** "Title Text Color Red" -> "Title Text Color"; "[blank, TextR0]" -> "Text Color". */
export function deriveTripletLabel(r: IDynamicLottieChange): string {
    const ph = (r.placeholder || '').trim();
    if (ph) {
        const trimmed = ph.replace(/\s+Red$/i, '').trim();
        return /Color$/i.test(trimmed) ? trimmed : trimmed + ' Color';
    }
    const base = (r.id || '').replace(/R\d*$/, '');
    return (humanize(base) || 'Color') + ' Color';
}

/**
 * Look ahead for matching G/B siblings (same base + same trailing-digit suffix)
 * of an R-row, both numeric and not yet consumed. Returns the triplet or null.
 * Only starts from R so each triplet is emitted exactly once.
 */
export function tryFindRgbTriplet(
    r: IDynamicLottieChange,
    pool: IDynamicLottieChange[],
    consumed: Set<IDynamicLottieChange>
): { r: IDynamicLottieChange; g: IDynamicLottieChange; b: IDynamicLottieChange } | null {
    const m = /^(.+?)R(\d*)$/.exec(r.id ?? '');
    if (!m) return null;
    const base = m[1];
    const tail = m[2];
    if (!base) return null;
    if (!valueLooksNumeric(r)) return null;

    const findChannel = (ch: 'G' | 'B'): IDynamicLottieChange | undefined =>
        pool.find((x) => !consumed.has(x) && x !== r && x.id === base + ch + tail && valueLooksNumeric(x));

    const g = findChannel('G');
    const b = findChannel('B');
    if (!g || !b) return null;
    return { r, g, b };
}

export function buildEditorRows(changes: IDynamicLottieChange[]): EditorRow[] {
    const consumed = new Set<IDynamicLottieChange>();
    const out: EditorRow[] = [];

    for (const c of changes) {
        if (consumed.has(c)) continue;

        const triplet = tryFindRgbTriplet(c, changes, consumed);
        if (triplet) {
            const ids = [triplet.r.id, triplet.g.id, triplet.b.id].join(' / ');
            out.push(new RgbRow(deriveTripletLabel(triplet.r), ids, triplet.r, triplet.g, triplet.b));
            consumed.add(triplet.r);
            consumed.add(triplet.g);
            consumed.add(triplet.b);
            continue;
        }

        out.push(buildSingleRow(c));
        consumed.add(c);
    }
    return out;
}

/**
 * Pick the right row class for a single change. Useful when the host UI needs
 * one inline control per slot (e.g. the project-template default-value editor)
 * rather than the full list.
 */
export function buildSingleRow(c: IDynamicLottieChange): EditorRow {
    const label = displayLabel(c);
    const tooltip = (c as any).raw || c.id || '';
    const kind = classifyInputKind(c);
    switch (kind) {
        case 'color':
            return new ColorRow(label, tooltip, c);
        case 'number':
            return new NumberRow(label, tooltip, deriveRange(c), c);
        case 'toggle':
            return new ToggleRow(label, tooltip, c);
        default:
            return new TextRow(label, tooltip, c);
    }
}
